'use client';

import React from 'react';

// CSS keyframes for spark animations
const sparkStyles = `
  @keyframes sparkRise1 {
    0%   { transform: translateY(0) translateX(0) scale(1); opacity: 1; }
    60%  { opacity: 0.8; }
    100% { transform: translateY(-120px) translateX(8px) scale(0.2); opacity: 0; }
  }
  @keyframes sparkRise2 {
    0%   { transform: translateY(0) translateX(0) scale(1); opacity: 0.9; }
    50%  { opacity: 0.7; }
    100% { transform: translateY(-100px) translateX(-12px) scale(0.15); opacity: 0; }
  }
  @keyframes sparkRise3 {
    0%   { transform: translateY(0) translateX(0) scale(1); opacity: 0.85; }
    55%  { opacity: 0.6; }
    100% { transform: translateY(-140px) translateX(5px) scale(0.1); opacity: 0; }
  }
  @keyframes sparkRise4 {
    0%   { transform: translateY(0) translateX(0) scale(1); opacity: 0.95; }
    65%  { opacity: 0.5; }
    100% { transform: translateY(-90px) translateX(-6px) scale(0.25); opacity: 0; }
  }
  @keyframes sparkRise5 {
    0%   { transform: translateY(0) translateX(0) scale(1); opacity: 0.8; }
    45%  { opacity: 0.9; }
    100% { transform: translateY(-160px) translateX(10px) scale(0.1); opacity: 0; }
  }
  @keyframes sparkPulse {
    0%, 100% { box-shadow: 0 0 4px 2px currentColor; }
    50%       { box-shadow: 0 0 8px 4px currentColor; }
  }
`;

// Shared spark particles component
const SparkParticles: React.FC<{ colors?: string[] }> = ({ colors = ['#FF6105', '#c89116', '#FF3A00'] }) => {
  const sparks = [
    { bottom: '8%',  left: '7%',  size: 5, color: colors[0], anim: 'sparkRise1', dur: '2.1s', delay: '0s' },
    { bottom: '12%', left: '91%', size: 4, color: colors[1], anim: 'sparkRise2', dur: '1.8s', delay: '0.4s' },
    { bottom: '5%',  left: '22%', size: 6, color: colors[2], anim: 'sparkRise3', dur: '2.4s', delay: '0.8s' },
    { bottom: '15%', left: '78%', size: 3, color: colors[0], anim: 'sparkRise4', dur: '1.6s', delay: '0.2s' },
    { bottom: '6%',  left: '45%', size: 5, color: colors[1], anim: 'sparkRise5', dur: '2.7s', delay: '1.0s' },
    { bottom: '10%', left: '60%', size: 4, color: colors[2], anim: 'sparkRise1', dur: '2.0s', delay: '1.4s' },
    { bottom: '4%',  left: '33%', size: 3, color: colors[0], anim: 'sparkRise2', dur: '1.9s', delay: '0.6s' },
    { bottom: '18%', left: '15%', size: 5, color: colors[1], anim: 'sparkRise3', dur: '2.3s', delay: '1.8s' },
    { bottom: '7%',  left: '85%', size: 4, color: colors[2], anim: 'sparkRise4', dur: '2.5s', delay: '0.3s' },
    { bottom: '13%', left: '52%', size: 6, color: colors[0], anim: 'sparkRise5', dur: '1.7s', delay: '1.2s' },
    { bottom: '9%',  left: '70%', size: 3, color: colors[1], anim: 'sparkRise1', dur: '2.2s', delay: '2.0s' },
    { bottom: '3%',  left: '38%', size: 5, color: colors[2], anim: 'sparkRise2', dur: '2.6s', delay: '0.9s' },
  ];

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: sparkStyles }} />
      {sparks.map((s, i) => (
        <div
          key={i}
          className="absolute pointer-events-none rounded-full"
          style={{
            bottom: s.bottom,
            left: s.left,
            width: s.size,
            height: s.size,
            backgroundColor: s.color,
            color: s.color,
            borderRadius: '50%',
            animation: `${s.anim} ${s.dur} ${s.delay} infinite ease-out, sparkPulse 1.2s ease-in-out infinite`,
            boxShadow: `0 0 ${s.size * 2}px ${s.size}px ${s.color}`,
            zIndex: 2,
          }}
        />
      ))}
    </>
  );
};

// Shared fire glow layers
const FireGlowLayers: React.FC<{ primary?: string; secondary?: string; tertiary?: string }> = ({
  primary = 'rgba(255,97,5,0.45)',
  secondary = 'rgba(200,145,22,0.3)',
  tertiary = 'rgba(255,97,5,0.25)',
}) => (
  <>
    <div
      className="absolute inset-0 pointer-events-none"
      style={{
        background: `radial-gradient(ellipse at 50% 100%, ${primary} 0%, rgba(255,58,0,0.2) 40%, transparent 70%)`,
        borderRadius: '18px',
      }}
    />
    <div
      className="absolute inset-0 pointer-events-none"
      style={{
        background: `radial-gradient(ellipse at 80% 20%, ${secondary} 0%, transparent 55%)`,
        borderRadius: '18px',
      }}
    />
    <div
      className="absolute inset-0 pointer-events-none"
      style={{
        background: `radial-gradient(ellipse at 10% 80%, ${tertiary} 0%, transparent 50%)`,
        borderRadius: '18px',
      }}
    />
  </>
);

// Gradient border card wrapper
interface GradientCardProps {
  children: React.ReactNode;
  gradient: string;
  borderWidth?: number;
}

const GradientCard: React.FC<GradientCardProps> = ({ children, gradient, borderWidth = 2 }) => {
  return (
    <div
      style={{
        background: gradient,
        padding: `${borderWidth}px`,
        borderRadius: '20px',
        margin: '8px',
      }}
    >
      <div style={{ borderRadius: '18px', overflow: 'hidden' }}>
        {children}
      </div>
    </div>
  );
};

// Section 1: Hero
const HeroSection: React.FC = () => {
  return (
    <GradientCard gradient="linear-gradient(135deg, #FF6105 0%, #c89116 30%, #FF3A00 60%, #FF6105 100%)" borderWidth={3}>
      <section
        className="relative w-full overflow-hidden"
        style={{
          borderRadius: '18px',
          background: 'radial-gradient(ellipse at 20% 50%, #1a0800 0%, #0d0300 40%, #050000 100%)',
          minHeight: '100vh',
        }}
      >
        <FireGlowLayers />
        <SparkParticles />

        {/* Content */}
        <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between w-full h-full px-5 sm:px-10 lg:px-16 py-12 sm:py-16" style={{ minHeight: '100vh' }}>
          {/* Left side - text */}
          <div className="flex flex-col justify-center w-full lg:max-w-lg xl:max-w-xl z-10" style={{ paddingTop: '40px' }}>
            <h1
              style={{
                fontFamily: "'CanvaSchoolFont', cursive",
                fontSize: 'clamp(36px, 6vw, 64px)',
                lineHeight: '1.05',
                marginBottom: '24px',
                background: 'linear-gradient(135deg, #ffffff 0%, #FFD580 40%, #FF6105 80%, #FF3A00 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                filter: 'drop-shadow(0 0 20px rgba(255,97,5,0.4))',
              }}
            >
              Learn how to use negativity as fuel for your fire.
            </h1>
            <p
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 'clamp(16px, 2.5vw, 24px)',
                color: 'rgba(255,235,200,0.92)',
                lineHeight: '1.5',
                marginBottom: '32px',
                textShadow: '0 1px 8px rgba(0,0,0,0.6)',
              }}
            >
              Ah, so you think darkness is your enemy? No. Darkness is the crucible where your true self emerges, like a phoenix from the ashes. In darkness, you discover your true strength, you learn to shine your light (no matter what happens). Your past, your pain, doesn&apos;t have to be something that chains you, it can be the fuel that forever lights your flame.
            </p>
            <a
              href="#pricing"
              className="inline-flex items-center justify-center transition-opacity hover:opacity-90"
              style={{
                background: 'linear-gradient(135deg, #FF6105, #c89116)',
                borderRadius: '100px',
                padding: '14px 32px',
                fontFamily: "'CanvaButton', sans-serif",
                fontSize: 'clamp(15px, 2vw, 18px)',
                fontWeight: '700',
                color: '#ffffff',
                textDecoration: 'none',
                width: 'fit-content',
                boxShadow: '0 0 20px rgba(255,97,5,0.5)',
              }}
            >
              Subscribe ➔
            </a>
            <p
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 'clamp(13px, 1.6vw, 16px)',
                color: 'rgba(255,210,140,0.88)',
                lineHeight: '1.65',
                marginTop: '16px',
                maxWidth: '420px',
                textShadow: '0 1px 6px rgba(0,0,0,0.5)',
                fontStyle: 'italic',
              }}
            >
              Nothing is stronger than the human soul on fire. Follow the program and choose to always ignite your fire.
            </p>
          </div>

          {/* Right side - hero image */}
          <div className="flex-shrink-0 mt-8 lg:mt-0 w-full lg:w-auto">
            <img
              src="/assets/images/102968-1788441454719.png"
              alt="Redeemed Bane hero image"
              className="w-full h-auto mx-auto"
              style={{ maxWidth: '617px', filter: 'drop-shadow(0 0 30px rgba(255,97,5,0.3))' }}
            />
          </div>
        </div>
      </section>
    </GradientCard>
  );
};

// Section 2: What We Do
const WhatWeDoSection: React.FC = () => {
  return (
    <div
      style={{
        background: 'linear-gradient(135deg, #FF6105 0%, #c89116 30%, #FF3A00 60%, #FF6105 100%)',
        padding: '3px',
        borderRadius: '20px',
        margin: '8px',
      }}
    >
      <section
        id="pricing"
        className="relative w-full overflow-hidden"
        style={{
          borderRadius: '18px',
          background: 'radial-gradient(ellipse at 80% 20%, #1a0800 0%, #0d0300 40%, #050000 100%)',
          minHeight: '100vh',
        }}
      >
        <FireGlowLayers
          primary="rgba(200,145,22,0.4)"
          secondary="rgba(255,97,5,0.35)"
          tertiary="rgba(255,58,0,0.2)"
        />
        <SparkParticles colors={['#c89116', '#FF6105', '#FFD580']} />

        <div className="relative z-10 w-full px-5 sm:px-10 lg:px-16 py-12 sm:py-16" style={{ minHeight: '100vh' }}>
          {/* Heading */}
          <h2
            className="mb-10"
            style={{
              fontFamily: "'Norwester', sans-serif",
              fontSize: 'clamp(28px, 5vw, 60px)',
              lineHeight: '1.1',
              textTransform: 'uppercase',
              background: 'linear-gradient(135deg, #ffffff 0%, #FFD580 40%, #FF6105 80%, #FF3A00 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              filter: 'drop-shadow(0 0 20px rgba(255,97,5,0.4))',
            }}
          >
            Subscribe and get unlimited access
          </h2>

          {/* Divider */}
          <div
            className="mb-10"
            style={{
              height: '2px',
              width: '120px',
              background: 'linear-gradient(to right, #FF6105, #c89116, transparent)',
              borderRadius: '2px',
              boxShadow: '0 0 10px rgba(255,97,5,0.5)',
            }}
          />

          {/* Pricing Tiers */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">

            {/* Bronze Tier */}
            <div
              className="flex flex-col relative"
              style={{
                borderRadius: '16px',
                background: 'linear-gradient(160deg, #1a0800 0%, #2a1200 60%, #1a0800 100%)',
                border: '1px solid rgba(176,100,20,0.5)',
                boxShadow: '0 0 30px rgba(176,100,20,0.2), inset 0 1px 0 rgba(176,100,20,0.15)',
                padding: 'clamp(24px, 3vw, 36px)',
                overflow: 'hidden',
              }}
            >
              {/* Glow accent */}
              <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(to right, transparent, rgba(176,100,20,0.8), transparent)' }} />
              {/* Badge */}
              <div className="mb-4 inline-flex w-fit items-center gap-2 px-3 py-1 rounded-full" style={{ background: 'rgba(176,100,20,0.2)', border: '1px solid rgba(176,100,20,0.4)' }}>
                <span style={{ fontSize: '16px' }}>🥉</span>
                <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '13px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#cd7f32' }}>Bronze</span>
              </div>
              {/* Price */}
              <div className="mb-1 flex items-end gap-1">
                <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 'clamp(42px, 5vw, 56px)', fontWeight: 800, lineHeight: 1, color: '#cd7f32' }}>$50</span>
              </div>
              <p className="mb-6" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '13px', color: 'rgba(205,127,50,0.7)', letterSpacing: '0.05em', textTransform: 'uppercase', fontWeight: 600 }}>One-time payment</p>
              {/* Divider */}
              <div className="mb-6" style={{ height: '1px', background: 'linear-gradient(to right, rgba(176,100,20,0.5), transparent)' }} />
              {/* Features */}
              <ul className="flex flex-col gap-3 mb-8 flex-1">
                {[
                  'Access to the REDEEM program',
                ].map((feature, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span style={{ color: '#cd7f32', fontSize: '16px', marginTop: '1px', flexShrink: 0 }}>✦</span>
                    <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 'clamp(14px, 1.5vw, 15px)', color: 'rgba(255,220,180,0.85)', lineHeight: '1.5' }}>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Silver Tier */}
            <div
              className="flex flex-col relative"
              style={{
                borderRadius: '16px',
                background: 'linear-gradient(160deg, #141414 0%, #1e1e1e 60%, #141414 100%)',
                border: '1px solid rgba(192,192,192,0.45)',
                boxShadow: '0 0 40px rgba(192,192,192,0.15), inset 0 1px 0 rgba(192,192,192,0.12)',
                padding: 'clamp(24px, 3vw, 36px)',
                overflow: 'hidden',
              }}
            >
              <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(to right, transparent, rgba(192,192,192,0.7), transparent)' }} />
              <div className="mb-4 inline-flex w-fit items-center gap-2 px-3 py-1 rounded-full" style={{ background: 'rgba(192,192,192,0.1)', border: '1px solid rgba(192,192,192,0.3)' }}>
                <span style={{ fontSize: '16px' }}>🥈</span>
                <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '13px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#c0c0c0' }}>Silver</span>
              </div>
              <div className="mb-1 flex items-end gap-1">
                <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 'clamp(42px, 5vw, 56px)', fontWeight: 800, lineHeight: 1, color: '#c0c0c0' }}>$50</span>
              </div>
              <p className="mb-6" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '13px', color: 'rgba(192,192,192,0.6)', letterSpacing: '0.05em', textTransform: 'uppercase', fontWeight: 600 }}>Per month</p>
              <div className="mb-6" style={{ height: '1px', background: 'linear-gradient(to right, rgba(192,192,192,0.4), transparent)' }} />
              <ul className="flex flex-col gap-3 mb-8 flex-1">
                {[
                  'Access to the REDEEM program',
                  'Unlimited access to the community forum',
                  'Receive support — Community members can create topics in the forum and I will drop in to offer guidance by replying.',
                  'Connect with other members',
                  'Share your own REDEEM journey',
                ].map((feature, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span style={{ color: '#c0c0c0', fontSize: '16px', marginTop: '1px', flexShrink: 0 }}>✦</span>
                    <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 'clamp(14px, 1.5vw, 15px)', color: 'rgba(230,230,230,0.85)', lineHeight: '1.5' }}>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Gold Tier — Featured */}
            <div
              className="flex flex-col relative"
              style={{
                borderRadius: '16px',
                background: 'linear-gradient(160deg, #1f0e00 0%, #2e1500 50%, #1f0e00 100%)',
                border: '1px solid rgba(255,193,7,0.55)',
                boxShadow: '0 0 50px rgba(255,193,7,0.2), 0 0 100px rgba(255,97,5,0.1), inset 0 1px 0 rgba(255,193,7,0.2)',
                padding: 'clamp(24px, 3vw, 36px)',
                overflow: 'hidden',
              }}
            >
              {/* Top glow line */}
              <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(to right, transparent, rgba(255,193,7,0.9), transparent)' }} />
              {/* Most Popular badge */}
              <div
                className="absolute top-4 right-4 px-3 py-1 rounded-full"
                style={{
                  background: 'linear-gradient(135deg, #FF6105, #c89116)',
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '11px',
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: '#fff',
                  boxShadow: '0 0 12px rgba(255,97,5,0.5)',
                }}
              >
                Best Value
              </div>
              <div className="mb-4 inline-flex w-fit items-center gap-2 px-3 py-1 rounded-full" style={{ background: 'rgba(255,193,7,0.12)', border: '1px solid rgba(255,193,7,0.4)' }}>
                <span style={{ fontSize: '16px' }}>🥇</span>
                <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '13px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#FFD700' }}>Gold</span>
              </div>
              <div className="mb-1 flex items-end gap-1">
                <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 'clamp(42px, 5vw, 56px)', fontWeight: 800, lineHeight: 1, background: 'linear-gradient(135deg, #FFD700, #FF6105)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>$500</span>
              </div>
              <p className="mb-6" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '13px', color: 'rgba(255,193,7,0.65)', letterSpacing: '0.05em', textTransform: 'uppercase', fontWeight: 600 }}>Per month</p>
              <div className="mb-6" style={{ height: '1px', background: 'linear-gradient(to right, rgba(255,193,7,0.5), rgba(255,97,5,0.3), transparent)' }} />
              <ul className="flex flex-col gap-3 mb-8 flex-1">
                {[
                  'Everything included in Silver',
                  '30 minute audio call via WhatsApp (once per week)',
                  'Ability to exchange private messages with Redeemed Bane (answered within 24 hours, Monday–Friday)',
                ].map((feature, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span style={{ color: '#FFD700', fontSize: '16px', marginTop: '1px', flexShrink: 0 }}>✦</span>
                    <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 'clamp(14px, 1.5vw, 15px)', color: 'rgba(255,235,180,0.9)', lineHeight: '1.5' }}>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

          </div>

          {/* Subscribe Now Section */}
          <div className="mt-12 text-center">
            <p
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 'clamp(13px, 1.4vw, 15px)',
                color: 'rgba(255,235,180,0.75)',
                marginBottom: '20px',
                fontStyle: 'italic',
              }}
            >
              All spiritual investments are final and non-refundable.
            </p>
            <h3
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 'clamp(22px, 3vw, 32px)',
                fontWeight: 900,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                background: 'linear-gradient(135deg, #FFD700, #FF6105)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                marginBottom: '24px',
              }}
            >
              SUBSCRIBE NOW
            </h3>
            <div className="flex flex-col items-center gap-4">
              <div className="flex items-start gap-3">
                <span
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: 'clamp(15px, 1.8vw, 18px)',
                    fontWeight: 700,
                    color: '#FF6105',
                    flexShrink: 0,
                    lineHeight: '1.6',
                  }}
                >
                  1.
                </span>
                <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 'clamp(15px, 1.8vw, 18px)', color: 'rgba(255,235,200,0.9)', lineHeight: '1.6', textAlign: 'left' }}>
                  Create an account on{' '}
                  <a
                    href="https://app.unifans.io/signup"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: '#FFD700',
                      fontWeight: 700,
                      textDecoration: 'underline',
                      textUnderlineOffset: '3px',
                    }}
                  >
                    UNIFANS
                  </a>
                </p>
              </div>
              <div className="flex items-start gap-3">
                <span
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: 'clamp(15px, 1.8vw, 18px)',
                    fontWeight: 700,
                    color: '#FF6105',
                    flexShrink: 0,
                    lineHeight: '1.6',
                  }}
                >
                  2.
                </span>
                <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 'clamp(15px, 1.8vw, 18px)', color: 'rgba(255,235,200,0.9)', lineHeight: '1.6', textAlign: 'left' }}>
                  Subscribe to{' '}
                  <a
                    href="https://app.unifans.io/c/redeemedbane"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: '#FFD700',
                      fontWeight: 700,
                      textDecoration: 'underline',
                      textUnderlineOffset: '3px',
                    }}
                  >
                    Redeemed Bane on Unifans
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Section: Why Buy The Redeem Program
const WhyBuySection: React.FC = () => {
  return (
    <div
      style={{
        background: 'linear-gradient(135deg, #FF6105 0%, #c89116 30%, #FF3A00 60%, #FF6105 100%)',
        padding: '3px',
        borderRadius: '20px',
        margin: '8px',
      }}
    >
      <section
        className="relative w-full overflow-hidden"
        style={{
          borderRadius: '18px',
          background: 'radial-gradient(ellipse at 20% 50%, #3d0a00 0%, #1a0500 40%, #0d0000 100%)',
          padding: 'clamp(40px, 6vw, 80px) clamp(20px, 5vw, 64px)',
        }}
      >
        {/* Animated fire glow layers */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse at 50% 100%, rgba(255,97,5,0.45) 0%, rgba(255,58,0,0.2) 40%, transparent 70%)',
            borderRadius: '18px',
          }}
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse at 80% 20%, rgba(200,145,22,0.3) 0%, transparent 55%)',
            borderRadius: '18px',
          }}
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse at 10% 80%, rgba(255,97,5,0.25) 0%, transparent 50%)',
            borderRadius: '18px',
          }}
        />

        {/* Spark particles */}
        <SparkParticles colors={['#FF6105', '#c89116', '#FF3A00']} />

        {/* Content */}
        <div className="relative z-10 max-w-4xl mx-auto">
          {/* Phoenix rising from ashes */}
          <div className="flex justify-center mb-8">
            <img
              src="/assets/images/phoenix-1788284182684.png"
              alt="Phoenix rising from the ashes"
              style={{
                width: 'clamp(160px, 20vw, 260px)',
                height: 'auto',
                filter: 'drop-shadow(0 0 28px rgba(255,97,5,0.7)) drop-shadow(0 0 60px rgba(255,58,0,0.4))',
                mixBlendMode: 'screen',
              }}
            />
          </div>

          {/* Title */}
          <h2
            className="mb-8"
            style={{
              fontFamily: "'Norwester', sans-serif",
              fontSize: 'clamp(28px, 5vw, 56px)',
              lineHeight: '1.1',
              textTransform: 'uppercase',
              letterSpacing: '-0.01em',
              background: 'linear-gradient(135deg, #ffffff 0%, #FFD580 40%, #FF6105 80%, #FF3A00 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              filter: 'drop-shadow(0 0 20px rgba(255,97,5,0.4))',
            }}
          >
            Why Buy The Redeem Program
          </h2>

          {/* Divider */}
          <div
            className="mb-8"
            style={{
              height: '2px',
              width: '120px',
              background: 'linear-gradient(to right, #FF6105, #c89116, transparent)',
              borderRadius: '2px',
              boxShadow: '0 0 10px rgba(255,97,5,0.5)',
            }}
          />

          {/* Body text */}
          <p
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 'clamp(16px, 2.2vw, 22px)',
              color: 'rgba(255,235,200,0.92)',
              lineHeight: '1.75',
              textShadow: '0 1px 8px rgba(0,0,0,0.6)',
            }}
          >
            The Redeem Program is about working on your mind, body and spirit.
            <br /><br />
            About being and staying true to yourself. Only by redeeming ourselves can we truly find peace. True freedom, abundance and peace is found within and only from that place can we find wholeness and create good in the world.
            <br /><br />
            This means the most important thing in life is the relationship with yourself and your ability to stay centered. If we learn to stay centered (remain peaceful) then we can truly always transform negativity into positivity. By choosing to follow The Redeem Program, you choose to commit to your true self, to peace and to become and always be a good example.
          </p>

          {/* Speech Bubble / Balloon */}
          <div className="mt-10 flex justify-center">
            <div className="relative inline-block max-w-xl w-full">
              {/* Balloon body */}
              <div
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.12) 0%, rgba(255,200,100,0.18) 100%)',
                  border: '2px solid rgba(255,180,60,0.55)',
                  borderRadius: '28px',
                  padding: 'clamp(18px, 3vw, 28px) clamp(20px, 4vw, 36px)',
                  boxShadow: '0 0 24px rgba(255,97,5,0.25), inset 0 0 16px rgba(255,200,80,0.08)',
                  backdropFilter: 'blur(6px)',
                  position: 'relative',
                }}
              >
                {/* Balloon string / tail pointing down-left */}
                <div
                  style={{
                    position: 'absolute',
                    bottom: '-22px',
                    left: '48px',
                    width: '0',
                    height: '0',
                    borderLeft: '14px solid transparent',
                    borderRight: '14px solid transparent',
                    borderTop: '22px solid rgba(255,180,60,0.55)',
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    bottom: '-18px',
                    left: '50px',
                    width: '0',
                    height: '0',
                    borderLeft: '12px solid transparent',
                    borderRight: '12px solid transparent',
                    borderTop: '20px solid rgba(255,255,255,0.07)',
                  }}
                />

                {/* Balloon text */}
                <p
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: 'clamp(14px, 1.8vw, 18px)',
                    color: 'rgba(255,240,200,0.97)',
                    lineHeight: '1.7',
                    textShadow: '0 1px 6px rgba(0,0,0,0.5)',
                    fontStyle: 'italic',
                    margin: 0,
                  }}
                >
                  "If you always choose to redeem yourself, then you can always continue to feel good about yourself, so follow The Redeem Program. You're always free to feel good! Start today."
                </p>
                <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'center' }}>
                  <a
                    href="#pricing"
                    style={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: 'clamp(13px, 1.5vw, 15px)',
                      fontWeight: 700,
                      color: '#1a0a00',
                      background: 'linear-gradient(135deg, #FFB830 0%, #FF6105 100%)',
                      padding: '10px 24px',
                      borderRadius: '50px',
                      textDecoration: 'none',
                      boxShadow: '0 0 16px rgba(255,97,5,0.45)',
                      display: 'inline-block',
                      letterSpacing: '0.03em',
                    }}
                  >
                    Subscribe now
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Section 4: Feedback/Testimonials
const FeedbackSection: React.FC = () => {
  const testimonials = [
    {
      quote: '"Thank you so much for all your services. I have been a member of the community for one year and have already turned my life around."',
      author: '- Elliiot S.',
    },
    {
      quote: '"The intervention guidance and support you provided is the reason why I have been sober for seven months today."',
      author: '- Nicholas N.',
    },
    {
      quote: '"I appreciate the freedom I get in sharing my stories and worries. Feeling the support of the community helps strengthen my resolve to continue my redeem journey."',
      author: '-Leslie B.',
    },
  ];

  return (
    <div
      style={{
        background: 'linear-gradient(135deg, #FF6105 0%, #c89116 30%, #FF3A00 60%, #FF6105 100%)',
        padding: '3px',
        borderRadius: '20px',
        margin: '8px',
      }}
    >
      <section
        className="relative w-full overflow-hidden"
        style={{
          borderRadius: '18px',
          background: 'radial-gradient(ellipse at 60% 30%, #1a0800 0%, #0d0300 40%, #050000 100%)',
          minHeight: '100vh',
        }}
      >
        <FireGlowLayers
          primary="rgba(255,97,5,0.4)"
          secondary="rgba(200,145,22,0.25)"
          tertiary="rgba(255,58,0,0.2)"
        />
        <SparkParticles />

        <div className="relative z-10 flex flex-col lg:flex-row w-full px-5 sm:px-10 lg:px-16 py-12 sm:py-16" style={{ minHeight: '100vh' }}>
          {/* Left: Image + Heading */}
          <div className="flex flex-col lg:w-1/2 lg:pr-12">
            <h2
              className="mb-8"
              style={{
                fontFamily: "'CanvaItalic', Georgia, serif",
                fontStyle: 'italic',
                fontSize: 'clamp(28px, 5vw, 60px)',
                lineHeight: '1.1',
                background: 'linear-gradient(135deg, #ffffff 0%, #FFD580 40%, #FF6105 80%, #FF3A00 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                filter: 'drop-shadow(0 0 20px rgba(255,97,5,0.4))',
              }}
            >
              See what our members have to say
            </h2>
            <div
              className="w-full"
              style={{
                maxWidth: '537px',
                borderRadius: '16px',
                overflow: 'hidden',
                boxShadow: '0 0 30px rgba(255,97,5,0.3)',
                border: '1px solid rgba(255,97,5,0.25)',
              }}
            >
              <img
                src="/assets/images/testimonials-cartoon.png"
                alt="Cartoon illustration of a diverse group of happy community members sharing their testimonials, surrounded by golden light and stars"
                className="w-full h-auto"
              />
            </div>
          </div>

          {/* Right: Testimonials */}
          <div className="flex flex-col justify-center lg:w-1/2 mt-8 lg:mt-0 gap-8">
            {testimonials.map((t, i) => (
              <div
                key={i}
                style={{
                  background: 'rgba(255,97,5,0.08)',
                  border: '1px solid rgba(255,97,5,0.2)',
                  borderRadius: '12px',
                  padding: '20px 24px',
                  boxShadow: '0 0 15px rgba(255,97,5,0.1)',
                }}
              >
                <p
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: 'clamp(15px, 2vw, 21.33px)',
                    color: 'rgba(255,235,200,0.9)',
                    lineHeight: '1.5',
                    marginBottom: '8px',
                    textShadow: '0 1px 8px rgba(0,0,0,0.6)',
                  }}
                >
                  {t.quote}
                </p>
                <p
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: 'clamp(15px, 2vw, 21.33px)',
                    color: '#FF6105',
                    lineHeight: '1.5',
                    fontWeight: '600',
                  }}
                >
                  {t.author}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

// Section 5: Details / CTA
const DetailsSection: React.FC = () => {
  return (
    <div
      style={{
        background: 'linear-gradient(135deg, #FF6105 0%, #c89116 30%, #FF3A00 60%, #FF6105 100%)',
        padding: '3px',
        borderRadius: '20px',
        margin: '8px',
      }}
    >
      <section
        className="relative w-full overflow-hidden"
        style={{
          borderRadius: '18px',
          background: 'radial-gradient(ellipse at 30% 70%, #1a0800 0%, #0d0300 40%, #050000 100%)',
          minHeight: '100vh',
        }}
      >
        <FireGlowLayers
          primary="rgba(255,58,0,0.35)"
          secondary="rgba(255,97,5,0.3)"
          tertiary="rgba(200,145,22,0.25)"
        />
        <SparkParticles colors={['#FF3A00', '#FF6105', '#c89116']} />

        <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between w-full px-5 sm:px-10 lg:px-16 py-12 sm:py-16" style={{ minHeight: '100vh' }}>
          {/* Left: Heading */}
          <div className="flex-1 w-full lg:pr-16">
            <div
              className="mb-6"
              style={{
                height: '2px',
                width: '80px',
                background: 'linear-gradient(to right, #FF6105, #c89116, transparent)',
                borderRadius: '2px',
                boxShadow: '0 0 10px rgba(255,97,5,0.5)',
              }}
            />
            <h2
              style={{
                fontFamily: "'CanvaItalic', Georgia, serif",
                fontStyle: 'italic',
                fontSize: 'clamp(28px, 5vw, 60px)',
                lineHeight: '1.1',
                background: 'linear-gradient(135deg, #ffffff 0%, #FFD580 40%, #FF6105 80%, #FF3A00 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                filter: 'drop-shadow(0 0 20px rgba(255,97,5,0.4))',
              }}
            >
              A strong support system makes things happen.
            </h2>
          </div>

          {/* Right: Description + Button */}
          <div className="flex-1 w-full mt-8 lg:mt-0">
            <p
              className="mb-8"
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 'clamp(16px, 2.5vw, 24px)',
                color: 'rgba(255,235,200,0.9)',
                lineHeight: '1.5',
                textShadow: '0 1px 8px rgba(0,0,0,0.6)',
              }}
            >
              Redeemed Bane is here to provide you with a solid support system, our community will guide you in your redeem journey.
            </p>
            <a
              href="#pricing"
              className="inline-flex items-center justify-center transition-opacity hover:opacity-90"
              style={{
                background: 'linear-gradient(135deg, #FF6105, #c89116)',
                borderRadius: '100px',
                padding: '14px 48px',
                fontFamily: "'CanvaButton', sans-serif",
                fontSize: 'clamp(15px, 2vw, 18px)',
                fontWeight: '700',
                color: '#ffffff',
                textDecoration: 'none',
                width: 'fit-content',
                boxShadow: '0 0 20px rgba(255,97,5,0.5)',
              }}
            >
              Subscribe ➔
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

// Section 6: Contact
const ContactSection: React.FC = () => {
  return (
    <div
      style={{
        background: 'linear-gradient(135deg, #FF6105 0%, #c89116 30%, #FF3A00 60%, #FF6105 100%)',
        padding: '3px',
        borderRadius: '20px',
        margin: '8px',
      }}
    >
      <section
        className="relative w-full overflow-hidden"
        style={{
          borderRadius: '18px',
          background: 'radial-gradient(ellipse at 50% 50%, #1a0800 0%, #0d0300 40%, #050000 100%)',
        }}
      >
        <FireGlowLayers
          primary="rgba(200,145,22,0.35)"
          secondary="rgba(255,97,5,0.3)"
          tertiary="rgba(255,58,0,0.2)"
        />
        <SparkParticles colors={['#c89116', '#FF6105', '#FFD580']} />

        <div className="relative z-10 flex flex-col lg:flex-row items-start justify-between w-full px-5 sm:px-10 lg:px-16 py-10 sm:py-12 gap-8">
          {/* Left: Connect with us + Instagram */}
          <div className="flex flex-col lg:w-1/2">
            {/* Contact Us heading */}
            <div className="mb-10">
              <h2
                style={{
                  fontFamily: "'CanvaItalic', Georgia, serif",
                  fontStyle: 'italic',
                  fontSize: 'clamp(32px, 5vw, 60px)',
                  lineHeight: '1.1',
                  background: 'linear-gradient(135deg, #ffffff 0%, #FFD580 40%, #FF6105 80%, #FF3A00 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  filter: 'drop-shadow(0 0 20px rgba(255,97,5,0.4))',
                }}
              >
                Contact Redeemed Bane
              </h2>
            </div>

            {/* Connect with us */}
            <div className="mt-auto">
              <p
                className="mb-4"
                style={{
                  fontFamily: "'CanvaItalic', Georgia, serif",
                  fontStyle: 'italic',
                  fontSize: 'clamp(20px, 3vw, 32px)',
                  color: '#FFD580',
                  lineHeight: '1.2',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  textShadow: '0 0 12px rgba(255,97,5,0.4)',
                }}
              >
                CONNECT WITH REDEEMED BANE
              </p>
              {/* Instagram Icon */}
              <a
                href="https://instagram.com/redeemedbane"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center"
                style={{
                  width: '48px',
                  height: '48px',
                  border: '2px solid rgba(255,97,5,0.6)',
                  borderRadius: '12px',
                  padding: '8px',
                  background: 'rgba(255,97,5,0.1)',
                  boxShadow: '0 0 12px rgba(255,97,5,0.3)',
                }}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ width: '28px', height: '28px' }}
                >
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5" stroke="#FF6105" strokeWidth="2" fill="none" />
                  <circle cx="12" cy="12" r="4" stroke="#FF6105" strokeWidth="2" fill="none" />
                  <circle cx="17.5" cy="6.5" r="1.5" fill="#FF6105" />
                </svg>
              </a>
            </div>
          </div>

          {/* Right: Contact details */}
          <div className="flex flex-col lg:w-1/2 gap-8">
            {/* Community Platform */}
            <div
              style={{
                background: 'rgba(255,97,5,0.08)',
                border: '1px solid rgba(255,97,5,0.2)',
                borderRadius: '12px',
                padding: '20px 24px',
              }}
            >
              <p
                className="mb-1"
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 'clamp(17px, 2.5vw, 26.67px)',
                  lineHeight: '1.3',
                  letterSpacing: '-0.02em',
                  fontWeight: '600',
                  background: 'linear-gradient(135deg, #FFD580, #FF6105)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                Community platform
              </p>
              <p
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 'clamp(14px, 2vw, 24px)',
                  color: 'rgba(255,235,200,0.85)',
                  lineHeight: '1.5',
                  wordBreak: 'break-all',
                }}
              >
                https://redeemedbane.com/forum
              </p>
            </div>

            {/* Email */}
            <div
              style={{
                background: 'rgba(255,97,5,0.08)',
                border: '1px solid rgba(255,97,5,0.2)',
                borderRadius: '12px',
                padding: '20px 24px',
              }}
            >
              <p
                className="mb-1"
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 'clamp(17px, 2.5vw, 26.67px)',
                  lineHeight: '1.3',
                  letterSpacing: '-0.02em',
                  fontWeight: '600',
                  background: 'linear-gradient(135deg, #FFD580, #FF6105)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                E-mail
              </p>
              <p
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 'clamp(14px, 2vw, 24px)',
                  color: 'rgba(255,235,200,0.85)',
                  lineHeight: '1.5',
                  wordBreak: 'break-all',
                }}
              >
                bearstrengthwithin@proton.me
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Joke Section
const JokeSection: React.FC = () => {
  return (
    <div
      style={{
        background: 'linear-gradient(135deg, #FF6105 0%, #c89116 30%, #FF3A00 60%, #FF6105 100%)',
        padding: '3px',
        borderRadius: '20px',
        margin: '8px',
      }}
    >
      <section
        className="relative w-full overflow-hidden"
        style={{
          borderRadius: '18px',
          background: 'radial-gradient(ellipse at 50% 0%, #1a0800 0%, #0d0300 40%, #050000 100%)',
        }}
      >
        <FireGlowLayers
          primary="rgba(255,97,5,0.35)"
          secondary="rgba(200,145,22,0.3)"
          tertiary="rgba(255,58,0,0.2)"
        />
        <SparkParticles />

        <div className="relative z-10 max-w-3xl mx-auto px-5 sm:px-8 py-10 text-center">
          {/* Quote */}
          <blockquote
            style={{
              color: 'rgba(255,235,200,0.9)',
              lineHeight: '1.75',
              fontStyle: 'italic',
              marginBottom: '24px',
              fontSize: 'clamp(15px, 2vw, 20px)',
              textShadow: '0 1px 8px rgba(0,0,0,0.6)',
            }}
          >
            &ldquo;Oh, you think darkness is your ally. But you merely adopted the dark; I was born in it, moulded by it. I didn&apos;t see the light until I was already a man, by then it was nothing to me but BLINDING! The shadows betray you, because they belong to me!&rdquo;
          </blockquote>
          <p
            style={{
              color: '#c89116',
              fontWeight: '600',
              marginBottom: '32px',
              fontSize: 'clamp(14px, 1.8vw, 16px)',
            }}
          >
            ~ Bane <span style={{ color: 'rgba(255,200,100,0.6)', fontWeight: 'normal' }}>(The Dark Knight Rises)</span>
          </p>

          {/* Divider */}
          <div
            className="mx-auto mb-8"
            style={{
              width: '64px',
              height: '2px',
              background: 'linear-gradient(to right, transparent, #c89116, transparent)',
              boxShadow: '0 0 8px rgba(200,145,22,0.5)',
            }}
          />

          {/* Personal punchline */}
          <p
            style={{
              color: 'rgba(255,235,200,0.85)',
              fontStyle: 'italic',
              fontSize: 'clamp(15px, 2vw, 18px)',
              textShadow: '0 1px 8px rgba(0,0,0,0.6)',
            }}
          >
            &ldquo;I say this line every time the power goes out.&rdquo;
          </p>
          <p
            style={{
              color: '#FF6105',
              fontWeight: '600',
              marginTop: '12px',
              fontSize: 'clamp(13px, 1.5vw, 14px)',
            }}
          >
            ~ Redeemed Bane
          </p>

          {/* Reflection */}
          <p
            style={{
              color: 'rgba(255,235,200,0.85)',
              lineHeight: '1.75',
              marginTop: '32px',
              fontSize: 'clamp(15px, 2vw, 18px)',
              textShadow: '0 1px 8px rgba(0,0,0,0.6)',
            }}
          >
            Now clearly this is a joke, but the point I&apos;m trying to make with this is that we can choose to look at things from a positive perspective and make it light and fun. Perception is everything. If you choose to transform negative thoughts into positive thoughts then your life will become more positive as well.
          </p>

          {/* Spiritual Alchemist */}
          <p
            style={{
              color: 'rgba(255,235,200,0.85)',
              lineHeight: '1.75',
              marginTop: '24px',
              fontSize: 'clamp(15px, 2vw, 18px)',
              textShadow: '0 1px 8px rgba(0,0,0,0.6)',
            }}
          >
            I aim to help people become a spiritual alchemist. You will enhance your ability to turn negative into positive. You will become immune to negativity. This way you can remain peaceful (remain centered) in every situation which will allow you to manifest more positive things into your life.
          </p>
        </div>
      </section>
    </div>
  );
};

// Footer
const Footer: React.FC = () => {
  return (
    <footer
      className="w-full flex items-center justify-between px-5 sm:px-8 py-3"
      style={{ backgroundColor: '#000000' }}
    >
    </footer>
  );
};

// Main Page
export default function Home() {
  return (
    <main className="w-full" style={{ backgroundColor: '#0a0a0a' }}>
      <HeroSection />
      <WhyBuySection />
      <JokeSection />
      <WhatWeDoSection />
      <FeedbackSection />
      <DetailsSection />
      <ContactSection />
      <Footer />
    </main>
  );
}
